import React, { useState, useEffect, useCallback } from "react";
import { makeAuthenticatedRequest } from "../../utils/apiRequest";
import { useAuth } from "../../contexts/authContext";

const GuideAccommodationPopup = ({ isOpen, onClose, itineraryId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [expenses, setExpenses] = useState([]);
// Track currency per expense (default: $)
const [currencyMap, setCurrencyMap] = useState({});
// Store USD rate for conversion (default 83 if not fetched)
const [usdRate, setUsdRate] = useState(83); // fallback to 83 if not found
// Helper to get tax percentage from API response
const getTaxPercentage = () => {
  const itineraryData = apiResponseData?.data?.[0] || apiResponseData;
  if (itineraryData && itineraryData.taxes && itineraryData.taxes.length > 0) {
    return itineraryData.taxes[0].tax_percentage || 0;
  }
  return 0;
};
  const [apiResponseData, setApiResponseData] = useState(null);

  // Sample data to display initially or as fallback
  const getSampleData = () => {
    return [
      {
        day: "01",
        date: "(20XX-XX-XX)",
        expenses: [
          { type: "Accommodation", amount: 150 },
          { type: "Taxi", amount: 50 }
        ]
      },
      {
        day: "02",
        date: "(20XX-XX-XX)",
        expenses: [
          { type: "Accommodation", amount: 150 },
          { type: "Taxi", amount: 50 }
        ]
      }
    ];
  };

  

  // Fetch guide accommodation data from API
  const fetchGuideAccommodation = useCallback(async () => {
    if (!token || !itineraryId) {
      setExpenses(getSampleData());
      return;
    }
    
    console.log('GuideAccommodationPopup: Starting to fetch guide accommodation data...');
    console.log('GuideAccommodationPopup: Using itinerary ID:', itineraryId);
    
    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/get_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ itinerary_id: itineraryId }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('GuideAccommodationPopup: API response received:', responseData);
      
      // Store the full response data in state for later use
      setApiResponseData(responseData);
      
      // The API response has data nested inside a 'data' array
      const itineraryData = responseData.data && responseData.data[0] ? responseData.data[0] : responseData;
      console.log('GuideAccommodationPopup: Extracted itinerary data:', itineraryData);

      // --- Extract USD rate from rates array in itinerary JSON ---
      if (Array.isArray(itineraryData.rates)) {
        const usdObj = itineraryData.rates.find(c => c.currency_name && c.currency_name.includes("USD"));
        if (usdObj && usdObj.value) {
          setUsdRate(Number(usdObj.value));
        } else {
          setUsdRate(83); // fallback if not found
        }
      } else {
        setUsdRate(83); // fallback if rates array missing
      }
      
      // Process the data from the itinerary_cost array which contains guide accommodation information
      if (itineraryData.itinerary_cost && Array.isArray(itineraryData.itinerary_cost)) {
        console.log('GuideAccommodationPopup: Valid guide accommodation data found in itinerary_cost:', itineraryData.itinerary_cost);
        
        // Process the API response data to match our component's expected format
        const formattedData = [];
        const newCurrencyMap = {};
        itineraryData.itinerary_cost.forEach((dayData, dayIndex) => {
          const guideExpenses = (dayData.guide_accommodation && Array.isArray(dayData.guide_accommodation))
            ? dayData.guide_accommodation
            : [
                { type: "Accommodation", amount: 0, baseAmount: 0, taxIncluded: false, currency: "$" },
                { type: "Taxi", amount: 0, baseAmount: 0, taxIncluded: false, currency: "$" }
              ];
          guideExpenses.forEach((e, expenseIndex) => {
            if (e.currency === 'RS') {
              newCurrencyMap[`${formattedData.length}-${expenseIndex}`] = 'RS';
            } else {
              newCurrencyMap[`${formattedData.length}-${expenseIndex}`] = '$';
            }
          });
          formattedData.push({
            day: dayData.day?.replace('Day ', '') || "--",
            date: dayData.date || "(----/--/--)",
            expenses: guideExpenses.map(e => {
              if (e.currency === 'RS') {
                return {
                  type: e.type || "Accommodation",
                  baseAmount: typeof e.baseAmount === "number"
                    ? e.baseAmount
                    : (typeof e.amount === "number" ? e.amount : 0),
                  amount: typeof e.final_usd_amount === "number" ? e.final_usd_amount : (typeof e.amount === "number" ? e.amount : 0),
                  enteredRS: typeof e.amount === "number" ? e.amount : 0,
                  taxIncluded: typeof e.taxIncluded === "boolean" ? e.taxIncluded : false
                };
              } else {
                return {
                  type: e.type || "Accommodation",
                  baseAmount: typeof e.baseAmount === "number"
                    ? e.baseAmount
                    : (typeof e.amount === "number" ? e.amount : 0),
                  amount: typeof e.amount === "number" ? e.amount : 0,
                  taxIncluded: typeof e.taxIncluded === "boolean" ? e.taxIncluded : false
                };
              }
            })
          });
        });
        if (formattedData.length === 0) {
          setExpenses(getSampleData());
        } else {
          setExpenses(formattedData);
          setCurrencyMap(newCurrencyMap);
        }
      } else {
        console.log('GuideAccommodationPopup: No valid guide accommodation data found in response');
        setExpenses(getSampleData());
      }
    } catch (err) {
      console.error('GuideAccommodationPopup: Error fetching guide accommodation data:', err.message);
      setError(err.message);
      setExpenses(getSampleData());
    } finally {
      console.log('GuideAccommodationPopup: Guide accommodation data fetch completed');
      setLoading(false);
    }
  }, [token, itineraryId]);

  useEffect(() => {
    if (isOpen) {
      fetchGuideAccommodation();
    }
  }, [isOpen, fetchGuideAccommodation]);

  // Format numbers to 2 decimal places with safety check
  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(parseFloat(value))) {
      return "0.00";
    }
    return parseFloat(value).toFixed(2);
  };

  // Track which input cell is being edited: { day: index, expense: index } | null
  const [editingCell, setEditingCell] = useState(null);

  // Helper: handle Enter key to blur input
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Handle currency toggle: input field always shows value in selected currency
  const handleCurrencyToggle = (dayIndex, expenseIndex) => {
    setCurrencyMap((prev) => {
      const key = `${dayIndex}-${expenseIndex}`;
      const current = prev[key] || '$';
      const newCurrency = current === '$' ? 'RS' : '$';
      
      setExpenses((prevExpenses) => {
        const updatedExpenses = [...prevExpenses];
        const expense = updatedExpenses[dayIndex].expenses[expenseIndex];
        const taxPercentage = getTaxPercentage();
        
        // Preserve tax calculations when switching currencies
        if (newCurrency === 'RS') {
          // Switching from USD to RS
          if (expense.amount !== undefined) {
            // Convert USD to RS
            const rsValue = expense.amount * usdRate;
            expense.enteredRS = rsValue;
            
            // Preserve tax calculations
            if (expense.taxIncluded) {
              expense.baseAmount = expense.baseAmount || (expense.amount / (1 + taxPercentage / 100));
            } else {
              expense.baseAmount = expense.amount;
            }
          }
        } else {
          // Switching from RS to USD
          if (expense.enteredRS !== undefined) {
            // Convert RS to USD
            const usdValue = expense.enteredRS / usdRate;
            expense.amount = usdValue;
            
            // Preserve tax calculations
            if (expense.taxIncluded) {
              expense.baseAmount = expense.baseAmount || (usdValue / (1 + taxPercentage / 100));
              expense.taxAmount = (usdValue - expense.baseAmount).toFixed(2);
            } else {
              expense.baseAmount = usdValue;
            }
          }
        }
        
        return updatedExpenses;
      });
      
      return { ...prev, [key]: newCurrency };
    });
  };


  // Editable table logic: always keep both USD and INR values in sync, and display value in selected currency
  const handleAmountChange = (dayIndex, expenseIndex, newAmount) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
      const expense = updatedExpenses[dayIndex].expenses[expenseIndex];
      const key = `${dayIndex}-${expenseIndex}`;
      const selectedCurrency = currencyMap[key] || '$';
      let amount = newAmount === '' ? '' : parseFloat(newAmount);
      if (selectedCurrency === 'RS') {
        // Input is Rs: update INR and USD values
        expense.amountINR = amount;
        expense.amountUSD = amount === '' ? '' : amount / usdRate;
        expense.amount = expense.amountUSD; // for legacy code compatibility
        expense.enteredRS = amount; // for legacy code compatibility
      } else {
        // Input is $: update USD and INR values
        expense.amountUSD = amount;
        expense.amountINR = amount === '' ? '' : amount * usdRate;
        expense.amount = amount; // for legacy code compatibility
        delete expense.enteredRS;
      }
      // Tax logic remains the same
      const taxPercentage = getTaxPercentage();
      let amountInUSD = expense.amountUSD || 0;
      if (expense.taxIncluded) {
        // amount is total, calculate base and tax
        const baseAmount = amountInUSD / (1 + taxPercentage / 100);
        expense.baseAmount = baseAmount;
        expense.taxAmount = (amountInUSD - baseAmount).toFixed(2);
      } else {
        // amount is base, calculate total
        expense.baseAmount = amountInUSD;
        delete expense.taxAmount;
      }
      setExpenses(updatedExpenses);
    }
  };



  // Handle tax checkbox toggle with Rs/$ recalculation and input update
  const handleTaxToggle = (dayIndex, expenseIndex) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
      const expense = updatedExpenses[dayIndex].expenses[expenseIndex];
      const key = `${dayIndex}-${expenseIndex}`;
      const selectedCurrency = currencyMap[key] || '$';
      const taxPercentage = getTaxPercentage();
      if (expense.taxIncluded) {
        // Was true, now will be false (removing tax)
        expense.taxIncluded = false;
        if (selectedCurrency === 'RS') {
          // Remove tax from enteredRS value
          const totalWithTax = expense.enteredRS !== undefined ? parseFloat(expense.enteredRS) : 0;
          const baseRS = totalWithTax / (1 + taxPercentage / 100);
          expense.enteredRS = baseRS;
          // Update USD value
          const amountInUSD = baseRS / usdRate;
          expense.baseAmount = amountInUSD;
          expense.amount = amountInUSD;
          delete expense.taxAmount;
        } else {
          // Remove tax from USD amount
          const totalWithTax = parseFloat(expense.amount || 0);
          const baseUSD = totalWithTax / (1 + taxPercentage / 100);
          expense.baseAmount = baseUSD;
          expense.amount = baseUSD;
          delete expense.taxAmount;
        }
      } else {
        // Was false, now will be true (adding tax)
        expense.taxIncluded = true;
        if (selectedCurrency === 'RS') {
          // Add tax to enteredRS value
          const baseRS = expense.enteredRS !== undefined ? parseFloat(expense.enteredRS) : 0;
          const totalWithTax = baseRS * (1 + taxPercentage / 100);
          expense.enteredRS = totalWithTax;
          // Update USD value
          const amountInUSD = totalWithTax / usdRate;
          expense.baseAmount = amountInUSD / (1 + taxPercentage / 100);
          expense.amount = amountInUSD;
          expense.taxAmount = (amountInUSD - expense.baseAmount).toFixed(2);
        } else {
          // Add tax to USD amount
          const baseUSD = parseFloat(expense.amount || 0);
          const totalWithTax = baseUSD * (1 + taxPercentage / 100);
          expense.baseAmount = baseUSD;
          expense.amount = totalWithTax;
          expense.taxAmount = (totalWithTax - baseUSD).toFixed(2);
        }
      }
      setExpenses(updatedExpenses);
    }
  };


  // Editable type logic
  const handleTypeChange = (dayIndex, expenseIndex, newType) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses[expenseIndex]) {
      updatedExpenses[dayIndex].expenses[expenseIndex].type = newType;
      setExpenses(updatedExpenses);
    }
  };

  // Add expense to a day
  const addExpense = (dayIndex) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex]) {
      updatedExpenses[dayIndex].expenses.push({ type: '', amount: 0 });
      setExpenses(updatedExpenses);
    }
  };

  // Remove expense from a day
  const removeExpense = (dayIndex, expenseIndex) => {
    const updatedExpenses = [...expenses];
    if (updatedExpenses[dayIndex] && updatedExpenses[dayIndex].expenses.length > 1) {
      updatedExpenses[dayIndex].expenses.splice(expenseIndex, 1);
      setExpenses(updatedExpenses);
    }
  };

  // Save changes to API (Rs/$ conversion as in OtherExpensesPopup)
  const saveChanges = async () => {
    if (!token || !itineraryId) {
      onClose();
      return;
    }
    setUpdating(true);
    setError(null);
    try {
      // Get the full itinerary data from the API response to preserve other data
      const itineraryData = apiResponseData?.data?.[0] || apiResponseData || {};
      // Create a deep copy to avoid mutating the original
      const updatedItineraryData = JSON.parse(JSON.stringify(itineraryData));
      // --- FLATTEN ALL EXPENSES INTO guide_accommodation ARRAY ---
      let guideAccommodationArr = [];
      let guideAccommodationTotal = 0;
      expenses.forEach((dayExpense, dayIndex) => {
        dayExpense.expenses.forEach((exp, expenseIndex) => {
          const currency = currencyMap[`${dayIndex}-${expenseIndex}`] || '$';
          let amount = 0;
          if (currency === 'RS') {
            amount = exp.enteredRS !== undefined && exp.enteredRS !== '' ? parseFloat(exp.enteredRS) : 0;
          } else {
            amount = exp.amount !== undefined && exp.amount !== '' ? parseFloat(exp.amount) : 0;
          }
          // USD conversion
          let final_usd_amount = 0;
          if (currency === 'RS') {
            final_usd_amount = amount / usdRate;
          } else {
            final_usd_amount = amount;
          }
          guideAccommodationArr.push({
            type: exp.type,
            amount: amount,
            final_usd_amount: parseFloat(final_usd_amount.toFixed(2)),
            taxIncluded: !!exp.taxIncluded,
            currency,
            manually_updated: true,
          });
          guideAccommodationTotal += final_usd_amount;
        });
      });
      updatedItineraryData.guide_accommodation = guideAccommodationArr;
      updatedItineraryData.guide_accommodation_total = guideAccommodationTotal.toFixed(2);
      // Also update each day in itinerary_cost with its own guide_accommodation and guide_accommodation_total
      if (Array.isArray(updatedItineraryData.itinerary_cost)) {
        expenses.forEach((dayExpense, dayIndex) => {
          const dayObj = updatedItineraryData.itinerary_cost[dayIndex];
          if (!dayObj) return;
          let dayGuideArr = [];
          let dayGuideTotal = 0;
          dayExpense.expenses.forEach((exp, expenseIndex) => {
            const currency = currencyMap[`${dayIndex}-${expenseIndex}`] || '$';
            let amount = 0;
            if (currency === 'RS') {
              amount = exp.enteredRS !== undefined && exp.enteredRS !== '' ? parseFloat(exp.enteredRS) : 0;
            } else {
              amount = exp.amount !== undefined && exp.amount !== '' ? parseFloat(exp.amount) : 0;
            }
            let final_usd_amount = 0;
            if (currency === 'RS') {
              final_usd_amount = amount / usdRate;
            } else {
              final_usd_amount = amount;
            }
            dayGuideArr.push({
              type: exp.type,
              amount: amount,
              final_usd_amount: parseFloat(final_usd_amount.toFixed(2)),
              taxIncluded: !!exp.taxIncluded,
              currency,
              manually_updated: true,
            });
            dayGuideTotal += final_usd_amount;
          });
          dayObj.guide_accommodation = dayGuideArr;
          dayObj.guide_accommodation_total = dayGuideTotal.toFixed(2);
        });
      }
      // Prepare payload
      const apiPayload = {
        itinerary_id: itineraryId,
        updates: updatedItineraryData,
        price_change: true
      };
      // Print the request body to console
      console.log("[GuideAccommodationPopup] update_itinerary_data body:", JSON.stringify(apiPayload, null, 2));
      // Send update request
      const response = await makeAuthenticatedRequest(
        "https://us-central1-evoq-travel-craft.cloudfunctions.net/update_itinerary_data",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(apiPayload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to update guide accommodation: ${response.status} - ${errorData.error || "Unknown error"}`);
      }
      // Optionally show a success message
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };


  // Calculate the total amount across all days and expense types (in USD)
  const getTotal = () => {
    let total = 0;
    expenses.forEach((day, dayIndex) => {
      day.expenses.forEach((expense, expenseIndex) => {
        const key = `${dayIndex}-${expenseIndex}`;
        const currency = currencyMap[key] || '$';
        let usdValue = 0;
        if (currency === 'RS') {
          // Use enteredRS if present, else fallback to amount * usdRate
          if (expense.enteredRS !== undefined && expense.enteredRS !== '') {
            usdValue = parseFloat(expense.enteredRS) / usdRate;
          } else if (expense.amount !== undefined && expense.amount !== '') {
            usdValue = parseFloat(expense.amount);
          }
        } else {
          usdValue = parseFloat(expense.amount);
        }
        if (!isNaN(usdValue)) {
          total += usdValue;
        }
      });
    });
    return total;
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Loading Guide Accommodation Data...</p>
        </div>
      </div>
    );
  }
  if (updating) {
    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white border border-blue-200 rounded-lg shadow-xl p-6 w-full max-w-md">
          <p className="text-blue-800 text-center">Updating Guide Accommodation Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out">
      <div
        className="bg-white border border-blue-200 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] relative transform transition-all duration-300 ease-in-out overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(to bottom right, #ffffff, #f7fbff)',
          boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5), 0 8px 10px -6px rgba(59, 130, 246, 0.3)'
        }}
      >
        {/* Close icon */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-blue-500 hover:text-blue-700 transition-colors z-10"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {/* Header */}
        <div className="p-4 border-b border-blue-200 sticky top-0 bg-white z-10 shadow-sm">
          <h2 className="text-blue-800 text-center font-bold text-xl uppercase tracking-wider">Guide Accommodation</h2>
        </div>
       <div className="flex-1 overflow-hidden p-4">
        {loading ? (
            <div className="text-center py-4">
              <p className="text-blue-600">Loading guide accommodation data...</p>
            </div>
          ) : error ? (
            <div className="bg-red-100 p-4 rounded mb-4 border border-red-300">
              <p className="text-red-700">{error}</p>
              <p className="text-sm mt-2 text-red-600">Error loading data. Please try again.</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(90vh-180px)] rounded-lg shadow-inner">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-blue-100">
                    <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0 w-20">
                      Day
                    </th>
                    <th className="border border-blue-200 p-3 text-blue-700 text-center font-medium text-xs uppercase tracking-wider sticky top-0">
                      Expenses
                    </th>
                    <th className="border border-blue-200 p-3 text-blue-700 text-right font-medium text-xs uppercase tracking-wider sticky top-0 w-28">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-100">
                  {expenses.map((day, dayIndex) => (
  <React.Fragment key={`day-${dayIndex}`}>
    {day.expenses.map((expense, expenseIndex) => (
      <tr
        key={`expense-${dayIndex}-${expenseIndex}`}
        className={`${expenseIndex % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-blue-100 transition-colors`}
      >
        {/* Only show day info on the first expense row for each day */}
        {expenseIndex === 0 ? (
          <td className="px-4 py-3 text-gray-800 align-top" rowSpan={day.expenses.length}>
            <div className="font-medium">{day.day}</div>
            <div className="text-xs text-gray-500">{day.date}</div>
          </td>
        ) : null}
        <td className="px-4 py-3 text-gray-800">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={expense.type || ''}
              onChange={(e) => handleTypeChange(dayIndex, expenseIndex, e.target.value)}
              className="flex-grow px-2 py-1 border border-blue-200 rounded hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
              placeholder="Expense type"
            />
            <button
              onClick={() => removeExpense(dayIndex, expenseIndex)}
              className="text-red-500 hover:text-red-700 focus:outline-none"
              title="Remove expense"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </td>
        <td className="px-4 py-3 text-gray-800">
          <div className="flex items-center justify-end space-x-2">
            {/* Toggle switch for currency */}
            <button
              type="button"
              onClick={() => handleCurrencyToggle(dayIndex, expenseIndex)}
              className={`relative w-12 h-6 flex items-center rounded-full focus:outline-none transition border border-blue-300 ${((currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$') ? 'bg-blue-200' : 'bg-green-200'}`}
              title="Toggle currency"
              style={{ minWidth: 48 }}
            >
              <span className={`absolute left-1 text-xs font-bold transition-colors ${(currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$' ? 'text-blue-900' : 'text-gray-400'}`}>$</span>
              <span className={`absolute right-1 text-xs font-bold transition-colors ${(currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === 'RS' ? 'text-green-900' : 'text-gray-400'}`}>RS</span>
              <span
                className={`absolute transition-transform duration-200 ease-in-out bg-white h-5 w-5 rounded-full shadow ${((currencyMap[`${dayIndex}-${expenseIndex}`] || '$') === '$') ? 'translate-x-0' : 'translate-x-6'}`}
                style={{ left: 0, top: 0.5 }}
              ></span>
            </button>
            <div className="flex flex-col">
              <input
                type="number"
                min="0"
                step="0.01"
                value={(() => {
                  const key = `${dayIndex}-${expenseIndex}`;
                  const selectedCurrency = currencyMap[key] || '$';
                  // Always display value in selected currency
                  if (selectedCurrency === 'RS') {
                    // For RS currency, use enteredRS value directly
                    if (expense.enteredRS !== undefined) {
                      return expense.enteredRS === 0 ? '' : String(Number(expense.enteredRS));
                    }
                    // Fallback for legacy data or if enteredRS is not available
                    if (typeof expense.amount === 'number' && !isNaN(expense.amount)) {
                      return (expense.amount * usdRate) === 0 ? '' : String(Number((expense.amount * usdRate).toFixed(2)));
                    }
                    return '';
                  } else {
                    // For USD currency, use amount value directly
                    if (typeof expense.amount === 'number' && !isNaN(expense.amount)) {
                      return expense.amount === 0 ? '' : String(Number(expense.amount.toFixed(2)));
                    }
                    return '';
                  }
                })()}
                onChange={(e) => handleAmountChange(dayIndex, expenseIndex, e.target.value)}
                onFocus={() => setEditingCell({ day: dayIndex, expense: expenseIndex })}
                onBlur={() => setEditingCell(editingCell => (editingCell && editingCell.day === dayIndex && editingCell.expense === expenseIndex) ? null : editingCell)}
                onKeyDown={handleInputKeyDown}
                className="w-20 text-right border border-blue-200 rounded p-1 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition"
              />
              {/* Show conversion info under input for both $ and Rs, like Other Expenses */}
              {(!editingCell || editingCell.day !== dayIndex || editingCell.expense !== expenseIndex) && (
                <div className="flex flex-row items-center gap-2 text-xs text-gray-500 mt-1" style={{lineHeight:1}}>
                  {(() => {
                    const key = `${dayIndex}-${expenseIndex}`;
                    const selectedCurrency = currencyMap[key] || '$';
                    if (selectedCurrency === 'RS') {
                      // Input is Rs, show $ conversion
                      const rsVal = expense.enteredRS !== undefined ? parseFloat(expense.enteredRS) : (expense.amount * usdRate);
                      // Calculate USD value from RS value
                      const usdVal = rsVal && usdRate ? (parseFloat(rsVal) / usdRate).toFixed(2) : '0.00';
                      return (<>
                        <span>$ {usdVal}</span>
                        <span>=</span>
                        <span>{rsVal || '0.00'} / {usdRate}</span>
                      </>);
                    } else {
                      // Input is $, show Rs conversion
                      const usdVal = expense.amount;
                      // Calculate RS value from USD value
                      const rsVal = expense.enteredRS !== undefined ? expense.enteredRS : 
                                   (usdVal && usdRate ? (parseFloat(usdVal) * usdRate).toFixed(2) : '0.00');
                      return (<>
                        <span>Rs {rsVal}</span>
                        <span>=</span>
                        <span>{usdVal || '0.00'} × {usdRate}</span>
                      </>);
                    }
                  })()}
                </div>
              )}
            </div>
            {/* Show selected currency symbol to the right */}
            <span className="ml-1 font-semibold text-xs text-gray-700">{currencyMap[`${dayIndex}-${expenseIndex}`] || '$'}</span>
            <label className="flex items-center ml-2 text-xs text-blue-700">
              <input
                type="checkbox"
                checked={!!expense.taxIncluded}
                onChange={() => handleTaxToggle(dayIndex, expenseIndex)}
                className="h-4 w-4 text-blue-600 border-blue-300 rounded focus:ring-blue-500 ml-1"
              />
              <span className="ml-1">Tax Included</span>
            </label>
            {/* Show tax calculation only if not editing this cell */}
            {expense.taxIncluded && getTaxPercentage() > 0 && (!editingCell || editingCell.day !== dayIndex || editingCell.expense !== expenseIndex) && (
              <span className="ml-2 text-xs text-blue-600">(incl. {getTaxPercentage()}% tax)</span>
            )}
          </div>
        </td>
      </tr>
    ))}
    {/* Add expense button for each day */}
    <tr className="bg-gray-50">
      <td colSpan="3" className="px-4 py-2">
        <button
          onClick={() => addExpense(dayIndex)}
          className="w-full flex items-center justify-center px-3 py-1 border border-blue-300 text-blue-600 text-sm rounded hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Expense
        </button>
      </td>
    </tr>
  </React.Fragment>
))}
                  <tr className="bg-blue-100 sticky bottom-0 shadow-md">
                    <td colSpan="2" className="border border-blue-200 p-3 text-blue-800 text-center font-semibold">
                      Total
                    </td>
                    <td className="border border-blue-200 p-3 text-blue-800 text-right font-semibold">
                      $ {formatCurrency(getTotal())}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          </div>
        {/* Save and Cancel buttons - fixed at bottom */}
        <div className="p-4 border-t border-blue-200 flex justify-end space-x-3 bg-white sticky bottom-0 shadow-md z-10">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-blue-300 text-blue-600 rounded hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            onClick={saveChanges}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuideAccommodationPopup;